import { QUERY_KEYS } from "@/libraries/constant";
import { randInt } from "@/libraries/helper";
import type {
	CreatePatientPayloadSchema,
	UpdatePatientPayloadSchema,
} from "@/schemas";
import { patientService } from "@/services";
import type { GetCaseResponse, GetCasesResponse, Patient } from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

export const patientMutations = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_PATIENT_CREATE], {
		retry: 2,
		mutationFn: (payload: CreatePatientPayloadSchema) =>
			patientService.create(payload),
		onMutate: async (updatedPatient) => {
			if (!updatedPatient.caseId) return;
			const { caseId, ...patientInfo } = updatedPatient;
			const patient = patientInfo as unknown as Patient;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
			const optimisticPatient = {
				...patient,
				id: randInt(),
			};
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) => ({
								...c,
								patient: c.id === caseId ? optimisticPatient : c.patient,
							})),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, caseId],
				(oldData) =>
					!oldData
						? undefined
						: { ...oldData, patient: { ...oldData.patient, ...patient } },
			);

			return { optimisticPatient };
		},
		onSuccess: (res, variable) => {
			if (!res || !variable?.caseId) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) => ({
								...c,
								patient: c.id === variable.caseId ? res.patient : c.patient,
							})),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variable.caseId],
				(oldData) =>
					!oldData ? undefined : { ...oldData, patient: res.patient },
			);
		},
		onError: (_, variable, context) => {
			if (!variable?.caseId || !context?.optimisticPatient) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) =>
								c.id === variable.caseId ? { ...c, patient: null } : c,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variable.caseId],
				(oldData) =>
					!oldData
						? undefined
						: {
								...oldData,
								patient: null,
							},
			);
		},
	});
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_PATIENT_UPDATE], {
		retry: 2,
		mutationFn: (payload: UpdatePatientPayloadSchema) =>
			patientService.update(payload),
		onMutate: async (updatedPatient) => {
			if (!updatedPatient.caseId) return;
			const { caseId, ...patientInfo } = updatedPatient;
			const patient = patientInfo as unknown as Patient;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASES],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_CASE, caseId],
			});
			const previousCase = queryClient.getQueryData<GetCaseResponse>([
				QUERY_KEYS.GET_CASE,
				caseId,
			]);
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) => ({
								...c,
								patient:
									c.id === caseId ? { ...c.patient, ...patient } : c.patient,
							})),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, caseId],
				(oldData) =>
					!oldData
						? undefined
						: { ...oldData, patient: { ...oldData.patient, ...patient } },
			);
			return { previousCase };
		},
		onSuccess: (res, variable) => {
			if (!res || !variable?.caseId) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) => ({
								...c,
								patient:
									c.id === variable.caseId
										? { ...c.patient, ...res.patient }
										: c.patient,
							})),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variable.caseId],
				(oldData) =>
					!oldData
						? undefined
						: { ...oldData, patient: { ...oldData.patient, ...res.patient } },
			);
		},
		onError: (_, variable, context) => {
			if (!variable?.caseId || !context?.previousCase) return;
			queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
				[QUERY_KEYS.GET_CASES],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							cases: p.cases.map((c) =>
								c.id === variable.caseId && context.previousCase
									? context.previousCase
									: c,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetCaseResponse>(
				[QUERY_KEYS.GET_CASE, variable.caseId],
				(oldData) =>
					!oldData
						? undefined
						: {
								...oldData,
								...context.previousCase,
							},
			);
		},
	});
};
